import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  CircleDollarSign,
  CreditCard,
  DollarSign,
  Gauge,
  MessageCircle,
  Receipt,
  RefreshCw,
  Save,
  ShoppingBag,
  Target,
  TrendingDown,
  TrendingUp,
  UserMinus,
  UserPlus,
  Users,
  Wallet,
  XCircle
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

const fmt = (valor) =>
  Number(valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });

const percentual = (valor) =>
  `${Number(valor || 0).toLocaleString('pt-BR', {
    maximumFractionDigits: 1
  })}%`;

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  color = 'bg-[#007AFF]/10 text-[#007AFF]',
  onClick
}) {
  return (
    <Card
      className={`bg-[#141414] border-[#27272A] transition-all ${
        onClick
          ? 'cursor-pointer hover:border-[#52525B]'
          : 'hover:border-[#3F3F46]'
      }`}
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-500">
              {title}
            </p>

            <p className="text-2xl font-black font-['Barlow_Condensed'] tracking-tight text-white mt-1">
              {value}
            </p>

              {subtitle && (
                <div className="text-xs text-zinc-500 mt-1">
                  {subtitle}
                </div>
              )}
          </div>

          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${color}`}
          >
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SectionTitle({ children, action }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-4">
      <h2 className="text-lg font-bold font-['Barlow_Condensed'] tracking-tight uppercase text-white">
        {children}
      </h2>

      {action}
    </div>
  );
}

function RankingList({
  items = [],
  labelKey,
  emptyText = 'Nenhum dado encontrado.'
}) {
  if (!items.length) {
    return (
      <p className="text-sm text-zinc-500">
        {emptyText}
      </p>
    );
  }

  const maior = Math.max(
    ...items.map((item) => Number(item.valor || 0)),
    1
  );

  return (
    <div className="space-y-3">
      {items.map((item, index) => {
        const largura =
          (Number(item.valor || 0) / maior) * 100;

        return (
          <div key={`${item[labelKey]}-${index}`}>
            <div className="flex items-center justify-between gap-3 mb-1">
              <p className="text-sm text-zinc-300 truncate">
                {item[labelKey]}
              </p>

              <p className="text-sm font-bold text-white shrink-0">
                {fmt(item.valor)}
              </p>
            </div>

            <div className="h-2 rounded-full bg-[#27272A] overflow-hidden">
              <div
                className="h-full rounded-full bg-[var(--cor-primaria,#007AFF)]"
                style={{ width: `${Math.max(2, largura)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-[#0A0A0A] border border-[#3F3F46] rounded-lg p-3 shadow-xl">
      <p className="text-xs font-bold text-white mb-2">
        {label}
      </p>

      {payload.map((item) => (
        <p
          key={item.dataKey}
          className="text-xs text-zinc-300"
        >
          {item.name}: {fmt(item.value)}
        </p>
      ))}
    </div>
  );
}


function AuditoriaGestao({ auditoria }) {
  const [diagnosticoAberto, setDiagnosticoAberto] = useState(false);

  if (!auditoria) return null;

  const q = auditoria.quantidades || {};
  const d = auditoria.detalhes || {};

  const problemas = [];

  const adicionar = (
    chave,
    nivel,
    titulo,
    descricao
  ) => {
    const quantidade = Number(q[chave] || 0);

    if (quantidade > 0) {
      problemas.push({
        chave,
        nivel,
        titulo: `${quantidade} ${titulo}`,
        descricao,
        itens: d[chave] || []
      });
    }
  };

  adicionar(
    'alunos_sem_mensalidade',
    'critico',
    'aluno(s) sem mensalidade',
    'Aluno(s) ativo(s) sem lançamento de mensalidade na competência atual.'
  );

  adicionar(
    'mensalidades_duplicadas',
    'critico',
    'mensalidade(s) duplicada(s)',
    'Existe mais de uma mensalidade válida para o mesmo aluno na competência atual.'
  );

  adicionar(
    'mensalidades_sem_aluno',
    'critico',
    'mensalidade(s) sem aluno',
    'Existem lançamentos financeiros sem vínculo com um aluno.'
  );

  adicionar(
    'mensalidades_aluno_inexistente',
    'critico',
    'mensalidade(s) com aluno inválido',
    'Existem lançamentos vinculados a alunos inexistentes ou inativos.'
  );

  adicionar(
    'mensalidades_sem_nome',
    'atencao',
    'mensalidade(s) sem nome',
    'Existem lançamentos sem o nome do aluno identificado.'
  );

  adicionar(
    'mensalidades_sem_plano',
    'atencao',
    'mensalidade(s) sem plano',
    'Existem lançamentos financeiros sem plano identificado.'
  );

  adicionar(
    'alunos_sem_plano',
    'atencao',
    'aluno(s) sem plano',
    'Aluno(s) ativo(s) sem plano de pagamento cadastrado.'
  );

  adicionar(
    'alunos_plano_invalido',
    'critico',
    'aluno(s) com plano inválido',
    'O plano cadastrado não existe mais no sistema.'
  );

  adicionar(
    'alunos_sem_turma',
    'atencao',
    'aluno(s) sem turma',
    'Aluno(s) ativo(s) não estão vinculados a nenhuma turma.'
  );

  adicionar(
    'turmas_acima_capacidade',
    'atencao',
    'turma(s) acima da capacidade',
    'Existem turmas com quantidade de alunos superior ao limite cadastrado.'
  );

  return (
    <Card className="bg-[#141414] border-[#27272A]">
      <CardContent className="p-6">

          <div className="flex items-center justify-between gap-4 mb-5">
            <div>
              <h2 className="text-lg font-bold text-white">
                Saúde da gestão
              </h2>
              <p className="text-sm text-zinc-400 mt-1">
                Auditoria automática dos cadastros e lançamentos financeiros.
              </p>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-2xl font-black text-white">
                  {Number(auditoria.score || 0)}%
                </div>
                <div className="text-xs text-zinc-400">
                  {auditoria.label || 'Análise'}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setDiagnosticoAberto((aberto) => !aberto)}
                className="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-2 text-sm font-semibold text-green-400 hover:bg-green-500/20 transition"
              >
                Diagnóstico
                <span className="ml-2">
                  {diagnosticoAberto ? '▲' : '▼'}
                </span>
              </button>
            </div>
          </div>

        <div className="rounded-xl bg-black/30 border border-white/5 p-4 mb-4">
          <p className="text-sm text-zinc-300">
            {auditoria.mensagem ||
              'Análise automática da saúde dos dados.'}
          </p>

          <p className="text-xs text-zinc-500 mt-2">
            Competência analisada: {auditoria.competencia || 'atual'}
          </p>
        </div>

          {diagnosticoAberto && (
            <>
        {problemas.length === 0 ? (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
            <div className="flex items-center gap-2 text-emerald-400 font-semibold">
              <CheckCircle2 className="w-5 h-5" />
              Nenhum problema encontrado
            </div>
            <p className="text-sm text-zinc-400 mt-1">
              Os cadastros e lançamentos analisados estão consistentes.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {problemas.map((problema, indice) => {
              const critico = problema.nivel === 'critico';

              return (
                <div
                  key={`${problema.chave}-${indice}`}
                  className={`rounded-xl border p-4 ${
                    critico
                      ? 'border-red-500/30 bg-red-500/5'
                      : 'border-amber-500/30 bg-amber-500/5'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {critico ? (
                      <XCircle className="w-5 h-5 text-red-400" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                    )}

                    <h3
                      className={
                        critico
                          ? 'font-semibold text-red-400'
                          : 'font-semibold text-amber-400'
                      }
                    >
                      {problema.titulo}
                    </h3>
                  </div>

                  <p className="text-sm text-zinc-300 mt-2">
                    {problema.descricao}
                  </p>

                  {problema.itens?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {problema.itens.slice(0, 10).map((item, itemIndex) => (
                        <div
                          key={
                            item.id ||
                            item.aluno_id ||
                            item.nome ||
                            item.aluno_nome ||
                            itemIndex
                          }
                          className="rounded-lg bg-black/40 border border-white/5 p-3"
                        >
                          <p className="text-sm font-semibold text-white">
                            {item.nome ||
                              item.aluno_nome ||
                              'Registro sem identificação'}
                          </p>

                          {item.email && (
                            <p className="text-xs text-zinc-500 mt-1">
                              {item.email}
                            </p>
                          )}

                          {item.problema && (
                            <p className="text-xs text-red-300 mt-1">
                              Problema: {item.problema}
                            </p>
                          )}

                          {item.descricao && (
                            <p className="text-xs text-zinc-400 mt-1">
                              {item.descricao}
                            </p>
                          )}

                          {item.competencia && (
                            <p className="text-xs text-zinc-500 mt-1">
                              Competência: {item.competencia}
                            </p>
                          )}
                        </div>
                      ))}

                      {problema.itens.length > 10 && (
                        <p className="text-xs text-zinc-500">
                          Mostrando os primeiros 10 registros.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
            </>
          )}

      </CardContent>
    </Card>
  );
}

export default function FinanceiroPage() {
  const [stats, setStats] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [periodo, setPeriodo] = useState('mensal');
  const [loading, setLoading] = useState(true);
  const [salvandoMeta, setSalvandoMeta] = useState(false);
  const [meta, setMeta] = useState('');

  const carregar = useCallback(async ({
    silencioso = false
  } = {}) => {
    if (!silencioso) setLoading(true);

    try {
      const [dashboard, historicoResponse] =
        await Promise.all([
          api.get(
            `/gestor/financeiro/dashboard?periodo=${periodo}`
          ),
          api.get('/gestor/financeiro/historico')
        ]);

      setStats(dashboard.data);
      setHistorico(
        (historicoResponse.data || []).slice(0, 12)
      );
      setMeta(
        String(dashboard.data?.meta_mensal || '')
      );
    } catch {
      toast.error(
        'Não foi possível carregar o dashboard financeiro.'
      );
    } finally {
      if (!silencioso) setLoading(false);
    }
  }, [periodo]);

  useEffect(() => {
    carregar();

    const intervalo = setInterval(
      () => carregar({ silencioso: true }),
      30000
    );

    return () => clearInterval(intervalo);
  }, [carregar]);

  const salvarMeta = async () => {
    const valor = Number(
      String(meta).replace(',', '.')
    );

    if (Number.isNaN(valor) || valor < 0) {
      toast.error('Informe uma meta válida.');
      return;
    }

    setSalvandoMeta(true);

    try {
      await api.put('/gestor/financeiro/meta', {
        meta_mensal: valor
      });

      toast.success('Meta mensal atualizada.');
      carregar({ silencioso: true });
    } catch {
      toast.error('Não foi possível salvar a meta.');
    } finally {
      setSalvandoMeta(false);
    }
  };

  const abrirMensalidades = (status) => {
    const params = new URLSearchParams();

    if (status) params.set('status', status);

    window.location.href =
      `/gestor/mensalidades${
        params.toString()
          ? `?${params.toString()}`
          : ''
      }`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        
    

<Skeleton className="h-12 bg-[#141414]" />

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(
            (item) => (
              <Skeleton
                key={item}
                className="h-28 bg-[#141414]"
              />
            )
          )}
        </div>
      </div>
    );
  }
const semaforoClasses = {
    verde:
      'border-green-500/30 bg-green-500/5 text-green-400',
    amarelo:
      'border-amber-500/30 bg-amber-500/5 text-amber-400',
    vermelho:
      'border-red-500/30 bg-red-500/5 text-red-400'
  };

  const corSemaforo =
    semaforoClasses[stats?.semaforo?.status] ||
    semaforoClasses.amarelo;

  return (

    <div
      className="space-y-6 animate-fadeIn"
      data-testid="financeiro-page"
    >


<div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">
            Dashboard Financeiro
          </h1>

          <p className="text-sm text-zinc-500 mt-1">
            Visão executiva de receitas, despesas,
            inadimplência e crescimento.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Select
            value={periodo}
            onValueChange={setPeriodo}
          >
            <SelectTrigger className="w-40 bg-[#0A0A0A] border-[#27272A]">
              <SelectValue />
            </SelectTrigger>

            <SelectContent className="bg-[#141414] border-[#27272A]">
              <SelectItem value="mensal">
                Mensal
              </SelectItem>
              <SelectItem value="trimestral">
                Trimestral
              </SelectItem>
              <SelectItem value="anual">
                Anual
              </SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="icon"
            onClick={() => carregar()}
            className="border-[#27272A]"
            title="Atualizar"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Card className={`border ${corSemaforo}`}>
        <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-current/10 flex items-center justify-center">
              <Gauge className="w-6 h-6" />
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider font-bold opacity-70">
                Saúde financeira
              </p>

              <p className="text-xl font-black font-['Barlow_Condensed'] uppercase">
                {stats?.semaforo?.label}
              </p>

              <p className="text-xs opacity-70">
                {stats?.semaforo?.mensagem}
              </p>
            </div>
          </div>

          <div className="text-right">
            <p className="text-3xl font-black">
              {percentual(stats?.taxa_recebimento)}
            </p>
            <p className="text-xs opacity-70">
              taxa de recebimento
            </p>
          </div>
        </CardContent>
      </Card>

      <AuditoriaGestao auditoria={stats?.auditoria} />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        
        <StatCard
          title="Previsto no mês"
          value={fmt(stats?.previsao_mes)}
          subtitle="Mensalidades não canceladas"
          icon={Target}
        />

        <StatCard
          title="Recebido da referência atual"
          value={fmt(stats?.recebido_mes)}
          subtitle={percentual(
            stats?.taxa_recebimento
          )}
          icon={DollarSign}
          color="bg-green-500/10 text-green-400"
        />

        <StatCard
          title="Em aberto"
          value={fmt(stats?.total_aberto)}
          subtitle="Ainda dentro do prazo"
          icon={Receipt}
          color="bg-amber-500/10 text-amber-400"
          onClick={() =>
            abrirMensalidades(
              'aguardando_pagamento'
            )
          }
        />

        <StatCard
          title="Em atraso"
          value={fmt(stats?.total_atrasado)}
          subtitle={`${stats?.inadimplentes || 0} aluno(s) · ${percentual(
            stats?.percentual_inadimplencia
          )}`}
          icon={AlertTriangle}
          color="bg-red-500/10 text-red-400"
          onClick={() =>
            abrirMensalidades('atrasado')
          }
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          title="Faturamento do período"
          value={fmt(stats?.faturamento)}
          subtitle={`Mensalidades: ${fmt(
            stats?.receita_mensalidades
          )}`}
          icon={CircleDollarSign}
          color="bg-green-500/10 text-green-400"
        />

        <StatCard
          title="Despesas do período"
          value={fmt(stats?.total_despesas)}
          icon={TrendingDown}
          color="bg-red-500/10 text-red-400"
        />

        <StatCard
          title="Resultado"
          value={fmt(stats?.lucro)}
          subtitle={
            stats?.lucro >= 0
              ? 'Saldo positivo'
              : 'Saldo negativo'
          }
          icon={
            stats?.lucro >= 0
              ? TrendingUp
              : TrendingDown
          }
          color={
            stats?.lucro >= 0
              ? 'bg-green-500/10 text-green-400'
              : 'bg-red-500/10 text-red-400'
          }
        />

        <StatCard
          title="Ticket médio"
          value={fmt(stats?.ticket_medio)}
          subtitle={`${stats?.alunos_ativos || 0} alunos ativos`}
          icon={CreditCard}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          title="Recebido hoje"
          value={fmt(stats?.recebido_hoje)}
          subtitle={`Ontem: ${fmt(
            stats?.recebido_ontem
          )}`}
          icon={Wallet}
          color="bg-green-500/10 text-green-400"
        />

        <StatCard
          title="Últimos 7 dias"
          value={fmt(stats?.recebido_7_dias)}
          icon={CalendarClock}
        />

        <StatCard
          title="Últimos 30 dias"
          value={fmt(stats?.recebido_30_dias)}
          icon={Receipt}
        />

        <StatCard
          title="Receita da loja"
          value={fmt(stats?.receita_loja)}
          icon={ShoppingBag}
          color="bg-purple-500/10 text-purple-400"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="bg-[#141414] border-[#27272A] xl:col-span-2">
          <CardContent className="p-5">
            <SectionTitle>
              Meta mensal
            </SectionTitle>

            <div className="flex items-center gap-3 flex-wrap">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={meta}
                onChange={(event) =>
                  setMeta(event.target.value)
                }
                className="max-w-xs bg-[#0A0A0A] border-[#27272A]"
                placeholder="Meta mensal"
              />

              <Button
                onClick={salvarMeta}
                disabled={salvandoMeta}
                className="bg-[var(--cor-primaria,#007AFF)]"
              >
                <Save className="w-4 h-4 mr-2" />
                {salvandoMeta
                  ? 'Salvando...'
                  : 'Salvar meta'}
              </Button>
            </div>

            <div className="mt-5">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-zinc-400">
                  Realizado
                </span>

                <span className="text-white font-bold">
                  {percentual(
                    stats?.percentual_meta
                  )}
                </span>
              </div>

              <div className="h-3 rounded-full bg-[#27272A] overflow-hidden">
                <div
                  className="h-full rounded-full bg-[var(--cor-primaria,#007AFF)]"
                  style={{
                    width: `${Math.min(
                      100,
                      Math.max(
                        0,
                        Number(
                          stats?.percentual_meta || 0
                        )
                      )
                    )}%`
                  }}
                />
              </div>

              <div className="flex justify-between text-xs text-zinc-500 mt-2">
                <span>
                  Recebido: {fmt(stats?.recebido_mes)}
                </span>
                <span>
                  Meta: {fmt(stats?.meta_mensal)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-5">
            <SectionTitle>
              Crescimento
            </SectionTitle>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-green-400">
                  <UserPlus className="w-4 h-4" />
                  <span className="text-sm">
                    Novos alunos
                  </span>
                </div>

                <span className="text-xl font-bold text-white">
                  {stats?.novos_alunos || 0}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-red-400">
                  <UserMinus className="w-4 h-4" />
                  <span className="text-sm">
                    Cancelamentos
                  </span>
                </div>

                <span className="text-xl font-bold text-white">
                  {stats?.cancelamentos || 0}
                </span>
              </div>

              <div className="pt-3 border-t border-[#27272A] flex items-center justify-between">
                <span className="text-sm text-zinc-400">
                  Crescimento líquido
                </span>

                <span
                  className={`text-xl font-bold ${
                    stats?.saldo_crescimento >= 0
                      ? 'text-green-400'
                      : 'text-red-400'
                  }`}
                >
                  {stats?.saldo_crescimento > 0
                    ? '+'
                    : ''}
                  {stats?.saldo_crescimento || 0}
                  {' · '}
                  {percentual(
                    stats?.taxa_crescimento
                  )}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-5">
            <SectionTitle>
              Caixa previsto para 30 dias
            </SectionTitle>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg bg-green-500/5 border border-green-500/20 p-4">
                <p className="text-xs uppercase text-green-400">
                  A receber
                </p>
                <p className="text-xl font-bold text-white mt-1">
                  {fmt(stats?.receber_30_dias)}
                </p>
              </div>

              <div className="rounded-lg bg-red-500/5 border border-red-500/20 p-4">
                <p className="text-xs uppercase text-red-400">
                  A pagar
                </p>
                <p className="text-xl font-bold text-white mt-1">
                  {fmt(stats?.pagar_30_dias)}
                </p>
              </div>

              <div className="rounded-lg bg-[#007AFF]/5 border border-[#007AFF]/20 p-4">
                <p className="text-xs uppercase text-[#007AFF]">
                  Saldo previsto
                </p>
                <p
                  className={`text-xl font-bold mt-1 ${
                    stats?.saldo_previsto_30d >= 0
                      ? 'text-white'
                      : 'text-red-400'
                  }`}
                >
                  {fmt(
                    stats?.saldo_previsto_30d
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-5">
            <SectionTitle>
              Próximos vencimentos
            </SectionTitle>

            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-4xl font-black text-white">
                  {stats?.proximos_vencimentos
                    ?.quantidade || 0}
                </p>
                <p className="text-sm text-zinc-500">
                  mensalidades vencem amanhã
                </p>
              </div>

              <div className="text-right">
                <p className="text-xl font-bold text-amber-400">
                  {fmt(
                    stats?.proximos_vencimentos
                      ?.valor
                  )}
                </p>

                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#007AFF] mt-2"
                  onClick={() =>
                    abrirMensalidades(
                      'aguardando_pagamento'
                    )
                  }
                >
                  Ver mensalidades
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-5">
          <SectionTitle
            action={
              <Button
                variant="outline"
                size="sm"
                className="border-[#3F3F46]"
                onClick={() =>
                  abrirMensalidades()
                }
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Abrir central de cobranças
              </Button>
            }
          >
            Cobranças e lembretes
          </SectionTitle>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              title="Faltam 2 dias"
              value={
                stats?.resumo_cobrancas
                  ?.faltam_2_dias?.quantidade || 0
              }
              subtitle={fmt(
                stats?.resumo_cobrancas
                  ?.faltam_2_dias?.valor
              )}
              icon={CalendarClock}
              color="bg-green-500/10 text-green-400"
              onClick={() =>
                abrirMensalidades(
                  'aguardando_pagamento'
                )
              }
            />

            <StatCard
              title="Vencem hoje"
              value={
                stats?.resumo_cobrancas
                  ?.vence_hoje?.quantidade || 0
              }
              subtitle={fmt(
                stats?.resumo_cobrancas
                  ?.vence_hoje?.valor
              )}
              icon={Receipt}
              color="bg-amber-500/10 text-amber-400"
              onClick={() =>
                abrirMensalidades(
                  'aguardando_pagamento'
                )
              }
            />

            <StatCard
              title="Cobranças em atraso"
              value={
                stats?.resumo_cobrancas
                  ?.atrasadas?.quantidade || 0
              }
              subtitle={fmt(
                stats?.resumo_cobrancas
                  ?.atrasadas?.valor
              )}
              icon={AlertTriangle}
              color="bg-red-500/10 text-red-400"
              onClick={() =>
                abrirMensalidades('atrasado')
              }
            />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-5">
          <SectionTitle>
            Fluxo de caixa
          </SectionTitle>

          <div className="h-80">
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <BarChart
                data={stats?.grafico_fluxo || []}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#27272A"
                />
                <XAxis
                  dataKey="mes"
                  stroke="#A1A1AA"
                />
                <YAxis stroke="#A1A1AA" />
                <Tooltip
                  content={<CustomTooltip />}
                />
                <Legend />
                <Bar
                  dataKey="receita"
                  name="Receita"
                  fill="#22C55E"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="despesa"
                  name="Despesa"
                  fill="#EF4444"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-5">
          <SectionTitle>
            Evolução do saldo
          </SectionTitle>

          <div className="h-72">
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <LineChart
                data={stats?.grafico_fluxo || []}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#27272A"
                />
                <XAxis
                  dataKey="mes"
                  stroke="#A1A1AA"
                />
                <YAxis stroke="#A1A1AA" />
                <Tooltip
                  content={<CustomTooltip />}
                />
                <Line
                  type="monotone"
                  dataKey="saldo"
                  name="Saldo"
                  stroke="#007AFF"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-5">
            <SectionTitle>
              Receita por turma
            </SectionTitle>

            <RankingList
              items={
                stats?.receita_por_turma || []
              }
              labelKey="turma"
              emptyText="Nenhuma receita vinculada a turmas."
            />
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-5">
            <SectionTitle>
              Receita por plano
            </SectionTitle>

            <RankingList
              items={
                stats?.receita_por_plano || []
              }
              labelKey="plano"
              emptyText="Nenhuma receita vinculada a planos."
            />
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-5">
            <SectionTitle>
              Receita por categoria
            </SectionTitle>

            <RankingList
              items={
                stats?.receita_por_categoria || []
              }
              labelKey="categoria"
              emptyText="Nenhuma categoria encontrada."
            />
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-5">
            <SectionTitle>
              Receita por origem
            </SectionTitle>

            <RankingList
              items={
                stats?.receita_por_origem || []
              }
              labelKey="origem"
            />
          </CardContent>
        </Card>
      </div>

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-5">
          <SectionTitle
            action={
              <Button
                variant="ghost"
                size="sm"
                className="text-[#007AFF]"
                onClick={() =>
                  abrirMensalidades('atrasado')
                }
              >
                Ver todos
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            }
          >
            Maiores inadimplências
          </SectionTitle>

          {!stats?.maiores_inadimplentes?.length ? (
            <div className="py-8 text-center">
              <Users className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
              <p className="text-sm text-zinc-500">
                Nenhuma inadimplência encontrada.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#27272A]">
                    <th className="text-left py-3 text-xs uppercase text-zinc-500">
                      Aluno
                    </th>
                    <th className="text-center py-3 text-xs uppercase text-zinc-500">
                      Mensalidades
                    </th>
                    <th className="text-center py-3 text-xs uppercase text-zinc-500">
                      Dias em atraso
                    </th>
                    <th className="text-right py-3 text-xs uppercase text-zinc-500">
                      Valor
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {stats.maiores_inadimplentes.map(
                    (item) => (
                      <tr
                        key={item.aluno_id}
                        className="border-b border-[#27272A] last:border-0"
                      >
                        <td className="py-3 text-sm text-white">
                          {item.nome}
                        </td>
                        <td className="py-3 text-sm text-zinc-400 text-center">
                          {item.quantidade}
                        </td>
                        <td className="py-3 text-sm text-red-400 text-center">
                          {item.dias_atraso}
                        </td>
                        <td className="py-3 text-sm font-bold text-red-400 text-right">
                          {fmt(item.valor)}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-5">
          <SectionTitle>
            Histórico recente
          </SectionTitle>

          {!historico.length ? (
            <p className="text-sm text-zinc-500">
              Nenhuma movimentação registrada.
            </p>
          ) : (
            <div className="space-y-2">
              {historico.map((item, index) => {
                const despesa =
                  item.tipo === 'despesa' ||
                  Number(item.valor) < 0;

                return (
                  <div
                    key={`${item.id || index}-${index}`}
                    className="flex items-center justify-between gap-4 py-3 border-b border-[#27272A] last:border-0"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`w-2 h-2 rounded-full shrink-0 ${
                          despesa
                            ? 'bg-red-400'
                            : item.status === 'pago'
                              ? 'bg-green-400'
                              : 'bg-amber-400'
                        }`}
                      />

                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">
                          {item.descricao}
                        </p>

                        <p className="text-xs text-zinc-500">
                          {String(
                            item.data || ''
                          ).split('T')[0]}
                        </p>
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <p
                        className={`text-sm font-bold ${
                          despesa
                            ? 'text-red-400'
                            : 'text-green-400'
                        }`}
                      >
                        {despesa ? '- ' : '+ '}
                        {fmt(
                          Math.abs(
                            Number(item.valor || 0)
                          )
                        )}
                      </p>

                      <p className="text-xs text-zinc-500">
                        {item.status || item.tipo}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
